<template>
    <v-menu v-model="organizationMenu" offset-x nudge-top="8" :close-on-content-click="false">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingSpaces || fetchingOrgs"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeOrgOid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : 'mdi-domain' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingOrgs">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title class="d-flex align-center">
                                <b>{{ organizationName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="organizationActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToOrgGeneralSettings()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <OrganizationInvite v-if="isOrgManager">
                                        <template v-slot:activator>
                                            <v-list-item @click="organizationActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </OrganizationInvite>
                                    <v-divider class="my-2"></v-divider>
                                    <EntityLeaveDialog entity-type="organization">
                                        <template v-slot:activator>
                                            <v-list-item @click="organizationActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-logout</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Leave organization</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityLeaveDialog>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ organizationName }}</span>
            </v-tooltip>
        </template>

        <v-list nav dense min-width="300" class="pb-0">
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-text-field
                        v-model="organizationSearch"
                        placeholder="Filter organizations..."
                        background-color="grey lighten-4"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense></v-text-field>
                </v-list-item-title>
            </v-list-item>
            <v-list-item>
                <v-list-item-title>ORGANIZATIONS</v-list-item-title>
            </v-list-item>
        </v-list>

        <v-list dense nav v-if="sortedOrgNames.length" class="organization-list-scroll pt-0">
            <v-list-item v-for="item in sortedOrgNames" :key="item.oid" @click="fetchOrgSpaces(item.long_id)">
                <v-list-item-content>
                    <v-list-item-title class="d-flex align-center">
                        {{ item.long_id }}
                    </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">{{ organizationRole(item.role).icon }}</v-icon>
                        </template>
                        <span>{{ organizationRole(item.role).label }}</span>
                    </v-tooltip>
                </v-list-item-action>
            </v-list-item>
        </v-list>

        <v-list v-else-if="organizationSearch && sortedOrgNames.length === 0" class="pb-4">
            <v-list-item>
                <v-list-item-title>
                    <dashboard-empty icon="mdi-information-outline">
                        No matching organizations found.
                        <br />
                        Try a different filter.
                    </dashboard-empty>
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script>
import { sortArray, organizationRole } from '@/utils'
import { mapGetters, mapState } from 'vuex'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'
import OrganizationInvite from '@/modules/home/components/OrganizationInvite.vue'

const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')

export default {
    name: 'OrganizationMenu',
    components: {
        DashboardEmpty,
        OrganizationInvite,
        EntityLeaveDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            organizationMenu: false,
            organizationSearch: '',
            organizationActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapState(['userOrgs', 'fetchingOrgs', 'currentOrgData']),
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapGetters('orgStore', ['orgLongName', 'isOrgManager']),
        organizationName() {
            if (this.$route.params.oid !== undefined) {
                return this.orgLongName
            }
            return this.currentOrgData?.long_id || this.orgNames[0]
        },
        orgNames() {
            return this.userOrgs
                .map(org => org.long_id)
                .sort(function (a, b) {
                    return a.toLowerCase().localeCompare(b.toLowerCase())
                })
        },
        sortedOrgNames() {
            if (this.organizationSearch.length) {
                return sortArray(this.userOrgs, 'long_id', 'ascending', true).filter(
                    org => org.long_id.toLowerCase().indexOf(this.organizationSearch.toLowerCase()) !== -1
                )
            }
            return sortArray(this.userOrgs, 'long_id', 'ascending', true)
        },
        activeOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        }
    },
    methods: {
        organizationRole,
        goToOrgGeneralSettings() {
            this.organizationActionsMenu = false
            if (!this.activeOrgOid) {
                return
            }
            this.$router.push({
                name: 'config-org-general',
                params: { oid: this.activeOrgOid }
            })
        },
        fetchOrgSpaces(orgName) {
            this.organizationMenu = false
            const org = this.userOrgs.find(item => item.long_id === orgName)
            if (!org) {
                return
            }

            if ((this.$route.name || '').startsWith('config')) {
                this.$router.push({
                    name: 'config-org-general',
                    params: { oid: org.oid }
                })
                return
            }

            this.$router.push({ name: 'home-dashboard', params: { oid: org.oid } })
        }
    },
    watch: {
        organizationMenu(val) {
            if (val) {
                this.organizationSearch = ''
            }
        }
    }
}
</script>

<style scoped lang="scss">
.organization-list-scroll {
    max-height: 60vh;
    overflow-y: auto;
}
</style>
